import { cn } from '../lib/cn';
import { STATUS_BADGE_REGISTRY, getStatusIconVisual, type StatusBadgeEntityType } from '../lib/badgeColors';
import { EntityStatusIcon } from './EntityStatusIcon';
import { Tooltip } from './Tooltip';

type EntityStatusBadgeSize = 'sm' | 'xs' | 'xxs';

type EntityStatusBadgeProps = {
  // 상태 보유 엔티티 타입. STATUS_BADGE_REGISTRY 키와 동일하다.
  entityType: StatusBadgeEntityType;
  // 원본 status 값(예: 'COMPLETED'). 레지스트리에서 글리프·컬러·라벨을 해석한다.
  status: string;
  size?: EntityStatusBadgeSize;
  // tooltip 문구를 덮어쓴다. 기본값은 해석된 상태 라벨(아이콘 전용이라 hover로 값을 확인).
  tooltip?: string;
  label?: string;
  className?: string;
};

// 크기별 글리프 크기. 텍스트 폴백(글리프가 없는 경우)에도 동일 토큰을 쓴다.
const TEXT_SIZE: Record<EntityStatusBadgeSize, string> = {
  sm: 'text-xs',
  xs: 'text-[11px]',
  xxs: 'text-[10px]',
};
// size별 글리프 크기 SSOT. EntityStatusSelect(편집형)도 이 매핑을 공유해 완료/완료외가 동일 크기로 렌더된다.
// sm = 목록/상세 헤더의 리딩 상태(18px), xs/xxs = 검색·관계·인라인 등 조밀한 컨텍스트.
export const STATUS_ICON_SIZE: Record<EntityStatusBadgeSize, 'xs' | 'sm' | 'md' | 'lg'> = {
  sm: 'lg',
  xs: 'sm',
  xxs: 'xs',
};

// 상태 보유 5개 엔티티(PLAN/COMPLETION_REPORT/POST_MORTEM/CO_ACTION/CODE_REVIEW)의 읽기 전용 상태 표기.
// PLAN과 동일하게 진행 글리프(EntityStatusIcon) 아이콘만 컬러로 렌더하고, 라벨은 tooltip으로만 노출한다(알약/텍스트 없음).
// 글리프·컬러는 STATUS_ICON_REGISTRY, 라벨은 STATUS_BADGE_REGISTRY에서 해석한다.
// 글리프가 없는 status(미지 값 등)는 아이콘이 없으므로 예외적으로 텍스트 라벨을 노출한다.
// 편집형 상태 컨트롤은 EntityStatusSelect를 사용한다(읽기 전용 시 이 컴포넌트로 위임).
export const EntityStatusBadge = ({
  entityType,
  status,
  size = 'sm',
  tooltip,
  label: labelOverride,
  className,
}: EntityStatusBadgeProps) => {
  const { label } = STATUS_BADGE_REGISTRY[entityType];
  const resolvedLabel = labelOverride ?? (label as Record<string, string>)[status] ?? status;
  const visual = getStatusIconVisual(entityType, status);
  const tip = tooltip ?? resolvedLabel;

  const content = visual ? (
    <span className={cn('inline-flex items-center', visual.color, className)} aria-label={resolvedLabel}>
      <EntityStatusIcon status={visual.icon} size={STATUS_ICON_SIZE[size]} />
    </span>
  ) : (
    // 글리프가 없으면 아이콘만으로는 의미 전달이 안 되므로 텍스트 라벨로 폴백한다.
    <span
      className={cn('inline-flex items-center font-medium text-[var(--color-fg-subtle)]', TEXT_SIZE[size], className)}
    >
      {resolvedLabel}
    </span>
  );

  return (
    <Tooltip content={tip} mode="overflowSafe" placement="top" className="inline-flex">
      {content}
    </Tooltip>
  );
};
